import { Fragment, useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import {
  NavigationMenuKey,
  NavigationPermissionMatrix,
  NavigationPermissionState,
  NAVIGATION_MENU_DEFINITIONS,
  NAVIGATION_POLICY_ROLES,
  SidebarPolicyRole,
  sanitizeNavigationPermissionMatrix,
  writeMatrixToStorage
} from '../../lib/navigationPolicy';

const STATE_CYCLE: NavigationPermissionState[] = ['inherit', 'allow', 'deny'];
const STATE_LABEL: Record<NavigationPermissionState, string> = {
  inherit: '0',
  allow: '+',
  deny: '-'
};

const STATE_CLASS: Record<NavigationPermissionState, string> = {
  inherit:
    'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800',
  allow:
    'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-900/50',
  deny:
    'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/50'
};

function nextState(current: NavigationPermissionState): NavigationPermissionState {
  const idx = STATE_CYCLE.indexOf(current);
  return STATE_CYCLE[(idx + 1) % STATE_CYCLE.length];
}

export default function NavigationPermissionsPanel() {
  const [matrix, setMatrix] = useState<NavigationPermissionMatrix>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/settings/navigation-permissions');
        if (!mounted) return;
        const parsed = sanitizeNavigationPermissionMatrix(res?.data?.data?.matrix || {});
        setMatrix(parsed);
        writeMatrixToStorage(parsed);
      } catch (error) {
        toast.error('Matrix konnte nicht geladen werden');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const groupedRows = useMemo(() => {
    const groups = new Map<string, typeof NAVIGATION_MENU_DEFINITIONS>();
    NAVIGATION_MENU_DEFINITIONS.forEach((item) => {
      const existing = groups.get(item.group) || [];
      existing.push(item);
      groups.set(item.group, existing);
    });
    return Array.from(groups.entries());
  }, []);

  const updateCell = (menuKey: NavigationMenuKey, role: SidebarPolicyRole) => {
    setMatrix((prev) => {
      const current = prev[menuKey]?.[role] || 'inherit';
      const next = nextState(current as NavigationPermissionState);
      return {
        ...prev,
        [menuKey]: {
          ...(prev[menuKey] || {}),
          [role]: next
        }
      };
    });
  };

  const saveMatrix = async () => {
    setSaving(true);
    try {
      const sanitized = sanitizeNavigationPermissionMatrix(matrix);
      await api.put('/settings/navigation-permissions', { matrix: sanitized });
      writeMatrixToStorage(sanitized);
      toast.success('Berechtigungs-Matrix gespeichert');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-50">Sidebar-Berechtigungen</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            `+` erlaubt, `0` vererbt bestehende Standard-Logik, `-` verbietet den Zugriff.
          </p>
        </div>
        <button
          type="button"
          onClick={saveMatrix}
          disabled={saving || loading}
          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
      </div>

      {loading ? (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-600 dark:text-gray-400">Lade Matrix...</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Menüpunkt</th>
                {NAVIGATION_POLICY_ROLES.map((role) => (
                  <th key={role.key} className="px-3 py-3 text-center font-semibold text-gray-700 dark:text-gray-300">
                    {role.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {groupedRows.map(([groupName, items]) => (
                <Fragment key={groupName}>
                  <tr key={`${groupName}-group`}>
                    <td colSpan={1 + NAVIGATION_POLICY_ROLES.length} className="bg-gray-50 dark:bg-gray-800/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {groupName}
                    </td>
                  </tr>
                  {items.map((item) => (
                    <tr key={item.key}>
                      <td className="px-4 py-2 text-gray-800 dark:text-gray-200">
                        <div className="font-medium">{item.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{item.href}</div>
                      </td>
                      {NAVIGATION_POLICY_ROLES.map((role) => {
                        const state = matrix[item.key]?.[role.key] || 'inherit';
                        return (
                          <td key={`${item.key}-${role.key}`} className="px-2 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => updateCell(item.key, role.key)}
                              className={`h-8 w-8 rounded-md border text-sm font-bold transition-colors ${STATE_CLASS[state]}`}
                              title={`${item.label} / ${role.label}: ${state}`}
                            >
                              {STATE_LABEL[state]}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
